import { z } from 'zod';

/**
 * Comunicação (M12). A regra que atravessa tudo: o sino do aplicativo é o
 * registro do que a igreja comunicou e não se desliga; os demais canais são
 * escolha de quem recebe.
 */

export const NOTIFICATION_CHANNELS = ['IN_APP', 'PUSH', 'EMAIL', 'WHATSAPP'] as const;
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

export const NOTIFICATION_CHANNEL_LABEL: Record<NotificationChannel, string> = {
  IN_APP: 'No aplicativo',
  PUSH: 'Notificação no celular',
  EMAIL: 'E-mail',
  WHATSAPP: 'WhatsApp',
};

/** Canais que a pessoa pode desligar. O sino fica. */
export const CANAIS_OPCIONAIS: readonly NotificationChannel[] = ['PUSH', 'EMAIL', 'WHATSAPP'];

/**
 * Tipos de acontecimento que geram mensagem. O catálogo é do produto; o texto
 * de cada um é da igreja.
 */
export const NOTIFICATION_TYPES = [
  'assignment.published',
  'assignment.reminder',
  'assignment.confirmation_due',
  'substitution.invited',
  'swap.requested',
  'swap.accepted',
  'swap.decided',
  'event.cancelled',
  'announcement',
  'whatsapp.session_lost',
  'child.incident',
  'child.guardian_call',
  'child.summary',
  'subscription.trial_ending',
  'subscription.read_only',
  'platform.support_access',
] as const;
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const NOTIFICATION_TYPE_LABEL: Record<NotificationType, string> = {
  'assignment.published': 'Fui escalado',
  'assignment.reminder': 'Lembrete do culto',
  'assignment.confirmation_due': 'Cobrança de confirmação',
  'substitution.invited': 'Convite para substituir',
  'swap.requested': 'Pedido de troca',
  'swap.accepted': 'Alguém aceitou a minha troca',
  'swap.decided': 'A liderança decidiu a troca',
  'event.cancelled': 'Culto cancelado',
  announcement: 'Comunicado da igreja',
  'whatsapp.session_lost': 'O WhatsApp da igreja caiu',
  'child.incident': 'Aconteceu algo com o meu filho no infantil',
  'child.guardian_call': 'O infantil está me chamando',
  'child.summary': 'Como foi o culto do meu filho',
  'subscription.trial_ending': 'A avaliação está acabando',
  'subscription.read_only': 'A igreja entrou em somente leitura',
  'platform.support_access': 'O suporte da El Shaday entrou na conta',
};

export const NOTIFICATION_STATUS = ['PENDING', 'SENT', 'READ', 'FAILED'] as const;
export const notificationStatusSchema = z.enum(NOTIFICATION_STATUS);
export type NotificationStatus = z.infer<typeof notificationStatusSchema>;

export const DELIVERY_STATUS = ['QUEUED', 'SENT', 'DELIVERED', 'FAILED', 'SKIPPED'] as const;
export const deliveryStatusSchema = z.enum(DELIVERY_STATUS);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  QUEUED: 'Na fila',
  SENT: 'Enviada',
  DELIVERED: 'Entregue',
  FAILED: 'Falhou',
  SKIPPED: 'Não enviada',
};

export const notificationSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  urgent: z.boolean(),
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationFeedSchema = z.object({
  items: z.array(notificationSchema),
  naoLidas: z.number().int(),
});
export type NotificationFeed = z.infer<typeof notificationFeedSchema>;

/** Preferência por canal e tipo. Ausente = padrão do produto. */
export const channelPreferenceSchema = z.object({
  channel: notificationChannelSchema,
  type: z.string().nullable(),
  enabled: z.boolean(),
});
export type ChannelPreference = z.infer<typeof channelPreferenceSchema>;

export const preferencesSchema = z.object({
  items: z.array(channelPreferenceSchema),
  /** Aparelhos com push ligado, para a pessoa reconhecer e revogar. */
  devices: z.array(
    z.object({
      id: z.string().uuid(),
      userAgent: z.string().nullable(),
      createdAt: z.string().datetime(),
      lastUsedAt: z.string().datetime().nullable(),
    }),
  ),
  /** Chave pública do Web Push, para o navegador se inscrever. */
  vapidPublicKey: z.string().nullable(),
  temEmail: z.boolean(),
  temWhatsApp: z.boolean(),
});
export type Preferences = z.infer<typeof preferencesSchema>;

export const setPreferencesSchema = z.object({
  items: z
    .array(
      z.object({
        channel: notificationChannelSchema,
        type: z.string().max(80).nullish(),
        enabled: z.boolean(),
      }),
    )
    .max(60),
});
export type SetPreferencesInput = z.infer<typeof setPreferencesSchema>;

export const subscribePushSchema = z.object({
  endpoint: z.string().url().max(500),
  keys: z.object({
    p256dh: z.string().min(10).max(200),
    auth: z.string().min(5).max(100),
  }),
});
export type SubscribePushInput = z.infer<typeof subscribePushSchema>;

// ── Comunicados ──────────────────────────────────────────────────────────────

export const ANNOUNCEMENT_STATUS = ['DRAFT', 'SENT'] as const;
export const announcementStatusSchema = z.enum(ANNOUNCEMENT_STATUS);
export type AnnouncementStatus = z.infer<typeof announcementStatusSchema>;

export const announcementSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  body: z.string(),
  status: announcementStatusSchema,
  urgent: z.boolean(),
  congregationId: z.string().uuid().nullable(),
  congregationName: z.string().nullable(),
  ministryIds: z.array(z.string().uuid()),
  ministryNames: z.array(z.string()),
  roleSlugs: z.array(z.string()),
  channels: z.array(notificationChannelSchema),
  recipients: z.number().int(),
  sentAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type Announcement = z.infer<typeof announcementSchema>;

export const createAnnouncementSchema = z.object({
  title: z.string().trim().min(3, 'Escreva um título').max(160),
  body: z.string().trim().min(3, 'Escreva a mensagem').max(4000),
  congregationId: z.string().uuid().nullish(),
  ministryIds: z.array(z.string().uuid()).max(20).default([]),
  roleSlugs: z.array(z.string().max(63)).max(20).default([]),
  channels: z.array(notificationChannelSchema).min(1).default(['IN_APP', 'PUSH']),
  urgent: z.boolean().default(false),
  /** Enviar agora ou guardar como rascunho. */
  enviar: z.boolean().default(true),
});
export type CreateAnnouncementInput = z.infer<typeof createAnnouncementSchema>;

/** Quantas pessoas o recorte alcança — mostrado antes de enviar. */
export const audienceSchema = z.object({
  total: z.number().int(),
  comEmail: z.number().int(),
  comPush: z.number().int(),
  comWhatsApp: z.number().int(),
});
export type Audience = z.infer<typeof audienceSchema>;

// ── Painel de entregas ───────────────────────────────────────────────────────

export const deliveryRowSchema = z.object({
  id: z.string().uuid(),
  personName: z.string(),
  type: z.string(),
  title: z.string(),
  channel: notificationChannelSchema,
  status: deliveryStatusSchema,
  skipReason: z.string().nullable(),
  error: z.string().nullable(),
  sentAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type DeliveryRow = z.infer<typeof deliveryRowSchema>;

export const notificationTemplateSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  channel: notificationChannelSchema,
  title: z.string(),
  body: z.string(),
  subject: z.string().nullable(),
  active: z.boolean(),
  /** Modelo do produto, ainda não reescrito pela igreja. */
  padrao: z.boolean(),
});
export type NotificationTemplate = z.infer<typeof notificationTemplateSchema>;

export const upsertTemplateSchema = z.object({
  type: notificationTypeSchema,
  channel: notificationChannelSchema,
  title: z.string().trim().min(3).max(160),
  body: z.string().trim().min(3).max(2000),
  subject: z.string().trim().max(200).nullish(),
  active: z.boolean().default(true),
});
export type UpsertTemplateInput = z.infer<typeof upsertTemplateSchema>;
